import { useState, useEffect, useCallback } from 'react'

interface UseOnlineStatusReturn {
  isOnline: boolean
  isOffline: boolean
  wasOffline: boolean // True if we went from offline -> online
  checkOnline: () => boolean
}

export function useOnlineStatus(): UseOnlineStatusReturn {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine)
  const [wasOffline, setWasOffline] = useState<boolean>(false)
  const [previousState, setPreviousState] = useState<boolean>(navigator.onLine)

  const checkOnline = useCallback((): boolean => {
    return navigator.onLine
  }, [])

  useEffect(() => {
    const handleOnline = () => {
      setPreviousState(isOnline)
      setIsOnline(true)
      setWasOffline(!previousState) // We were offline, now online
    }

    const handleOffline = () => {
      setPreviousState(isOnline)
      setIsOnline(false)
      setWasOffline(false)
    }

    // Listen for online/offline events
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Also check periodically (some browsers don't fire events reliably)
    const interval = setInterval(() => {
      const current = navigator.onLine
      if (current !== isOnline) {
        if (current) {
          handleOnline()
        } else {
          handleOffline()
        }
      }
    }, 5000)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      clearInterval(interval)
    }
  }, [isOnline, previousState])

  // Reset wasOffline flag after it's been consumed
  useEffect(() => {
    if (wasOffline && isOnline) {
      const timer = setTimeout(() => {
        setWasOffline(false)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [wasOffline, isOnline])

  return {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
    checkOnline,
  }
}

export default useOnlineStatus
